import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Droplet,
  ChevronRight,
  Fan,
  Gauge,
  Lightbulb,
  Filter,
  Timer,
  Settings,
  ShieldCheck,
  Truck,
  Phone,
  ArrowRight,
  Search
} from 'lucide-react';
import img from "../../image2/pump.jpg"
import img1 from "../../image2/shaft.jpeg"
import img2 from "../../image2/bask.jpg"
import img3 from "../../image2/filt.avif"
import img4 from "../../image2/card.jpeg"
import img5 from "../../image2/multi.webp"
import img6 from "../../image2/pb.jpg"
import img7 from "../../image2/lig.jpeg"
import img8 from "../../image2/lens.jpeg"
import { useNavigate } from 'react-router-dom';
const PoolSpares = () => {
  const [activeCategory, setActiveCategory] = useState('pumps');
  const navigate = useNavigate();
  const categories = [
    {
      id: 'pumps',
      title: 'Pumps & Motors',
      icon: <Fan className="w-5 h-5" />,
      description: 'Essential pump parts and motor components',
      products: [
        {
          title: 'Pump Impeller',
          description: 'High-performance replacement impeller for swimming pool pumps.',
          image: img,
          inStock: true,
          price: 'NPR'
        },
        {
          title: 'Motor Shaft Seal',
          description: 'Professional-grade shaft seal for pool pump motors.',
          image: img1,
          inStock: true,
          price: 'NPR'
        },
        {
          title: 'Pump Strainer Basket',
          description: 'Durable replacement strainer basket for debris collection.',
          image: img2,
          inStock: true,
          price: 'NPR'
        }
      ]
    },
    {
      id: 'filtration',
      title: 'Filter Systems',
      icon: <Filter className="w-5 h-5" />,
      description: 'Filter components and media',
      products: [
        {
          title: 'Sand Filter Element',
          description: 'Premium grade filter sand for optimal water clarity.',
          image: img3,
          inStock: true,
          price: 'NPR'
        },
        {
          title: 'Cartridge Filter',
          description: 'High-capacity replacement cartridge for pool filters.',
          image: img4,
          inStock: true,
          price: 'NPR'
        },
        {
          title: 'Multiport Valve',
          description: 'Six-position valve for complete filter control.',
          image: img5,
          inStock: true,
          price: 'NPR'
        }
      ]
    },
    {
      id: 'lighting',
      title: 'Pool Lighting',
      icon: <Lightbulb className="w-5 h-5" />,
      description: 'Lighting components and fixtures',
      products: [
        {
          title: 'LED Pool Bulb',
          description: 'Energy-efficient LED replacement bulb for pool lights.',
          image: img6,
          inStock: true,
          price: 'NPR'
        },
        {
          title: 'Light Housing Kit',
          description: 'Complete housing replacement kit for pool lights.',
          image: img7,
          inStock: true,
          price: 'NPR'
        },
        {
          title: 'Light Lens Cover',
          description: 'Waterproof lens cover for pool lighting fixtures.',
          image: img8,
          inStock: true,
          price: 'NPR'
        }
      ]
    }
  ];

  const activeProducts = categories.find(cat => cat.id === activeCategory)?.products || [];

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Hero Section */}
      <motion.section 
        className="relative min-h-[400px] bg-gradient-to-r from-blue-800 via-blue-600 to-blue-500 text-white overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        {/* Animated Water Pattern Background */}
        <div className="absolute inset-0 opacity-10">
          {[...Array(5)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute inset-0"
              initial={{ backgroundPositionX: "0%" }}
              animate={{ backgroundPositionX: "100%" }}
              transition={{
                duration: 15 + i * 3,
                repeat: Infinity,
                ease: "linear"
              }}
              style={{
                backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 1200 120' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 24c120 0 120 90 240 90s120-90 240-90 120 90 240 90 120-90 240-90 120 90 240 90V0H0v24z' fill='%23ffffff'/%3E%3C/svg%3E\")",
                backgroundRepeat: "repeat-x",
                backgroundSize: "1200px 120px",
                top: `${i * 25}%`,
                opacity: 0.2 - i * 0.03
              }}
            />
          ))}
        </div>

        <div className="container mx-auto px-6 h-full flex flex-col justify-center relative z-10 py-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              >
                <Fan className="w-12 h-12 text-blue-300" />
              </motion.div>
              <h1 className="text-4xl md:text-5xl font-bold">
                Pool Care Parts
              </h1>
            </div>
            <p className="text-lg md:text-xl mb-8 max-w-2xl text-blue-50">
              Premium quality replacement parts for your swimming pool equipment
            </p>
            <motion.div
              className="flex flex-wrap gap-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              
              <button className="border-2 border-white text-white px-6 py-3 rounded-lg font-semibold hover:bg-white/10 transition-all duration-300"
              onClick={() => navigate('/contact')}>
                Technical Support
              </button>
            </motion.div>
          </motion.div>
        </div>
      </motion.section>

      {/* Features Section */}
      <section className="py-16 px-6">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { icon: <ShieldCheck />, title: 'Genuine Parts', text: 'Manufacturer certified components' },
              { icon: <Truck />, title: 'Express Shipping', text: 'Next-day delivery available' },
              { icon: <Phone />, title: 'Expert Support', text: '24/7 technical assistance' },
              { icon: <Filter />, title: 'Easy Installation', text: 'Detailed guides included' }
            ].map((feature, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all group"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <div className="text-blue-600 mb-4 group-hover:scale-110 transition-transform">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-16 px-6 bg-gradient-to-b from-white to-blue-50">
        <div className="container mx-auto">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
          >
            <h2 className="text-3xl font-bold mb-4">Replacement Parts</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Find the exact replacement parts you need for your pool equipment
            </p>
          </motion.div>

          <div className="flex flex-wrap gap-4 justify-center mb-12">
            {categories.map((category) => (
              <motion.button
                key={category.id}
                className={`px-6 py-3 rounded-lg flex items-center gap-2 transition-all duration-300 ${
                  activeCategory === category.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-blue-50'
                }`}
                onClick={() => setActiveCategory(category.id)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {category.icon}
                {category.title}
              </motion.button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {activeProducts.map((product, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all"
                whileHover={{ scale: 1.02 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="relative">
                  <img
                    src={product.image}
                    alt={product.title}
                    className="w-full h-48 object-cover"
                  />
                  <div className={`absolute top-4 right-4 px-3 py-1 rounded-full text-sm ${
                    product.inStock ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
                  }`}>
                    {product.inStock ? 'In Stock' : 'Out of Stock'}
                  </div>
                  <div className="absolute bottom-4 left-4 bg-white/90 px-3 py-1 rounded-lg text-blue-900 font-bold">
                    {product.price}
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-semibold mb-2">{product.title}</h3>
                  <p className="text-gray-600 mb-4">{product.description}</p>
                  <div className="flex items-center justify-between">
                   
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default PoolSpares;